import { useEffect, useId, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";

const GLYPHS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ#$%";

/**
 * ScrambleCounter — defense-major scramble-to-lock text effect.
 * On first view, each character cycles through random glyphs before locking
 * to the target value. Inspired by CRT boot sequences and Palantir/Anduril UIs.
 */
export default function ScrambleCounter({
  value,
  duration = 1200,
  delay = 0,
  className = "",
  preserveSymbols = true,
  testId,
}) {
  const ref = useRef(null);
  const instanceId = useId();
  const reducedMotion = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [display, setDisplay] = useState(String(value));

  useEffect(() => {
    if (reducedMotion || !inView) { setDisplay(String(value)); return; }
    let frameId;
    let previousFrame = -Infinity;
    const startTime = performance.now() + delay;
    const target = String(value);

    const tick = (t) => {
      const elapsed = t - startTime;
      if (elapsed < 0) {
        frameId = requestAnimationFrame(tick);
        return;
      }
      const progress = Math.min(elapsed / duration, 1);
      if (progress < 1 && t - previousFrame < 40) { frameId = requestAnimationFrame(tick); return; }
      previousFrame = t;
      // Each character reveals after its proportional progress
      let out = "";
      for (let i = 0; i < target.length; i++) {
        const charProgress = (progress - (i / target.length) * 0.6) * 2.5;
        if (charProgress >= 1) {
          out += target[i];
        } else if (charProgress > 0) {
          const ch = target[i];
          if (preserveSymbols && /[+%.,\s/-]/.test(ch)) {
            out += ch;
          } else {
            out += GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
          }
        } else {
          // Not yet revealing — show empty space or a placeholder
          out += "·";
        }
      }
      setDisplay(out);
      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      } else {
        setDisplay(target);
      }
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [inView, value, duration, delay, preserveSymbols, reducedMotion]);

  return (
    <span
      ref={ref}
      className={`relative inline-block tabular-nums ${className}`}
      data-testid={testId || `scramble-counter-${instanceId.replace(/[^a-zA-Z0-9]/g, '')}`}
      data-counter-settled={display === String(value) ? 'true' : 'false'}
    >
      {/* Invisible target: reserves the final rendered width so the
          scramble animation can't change the layout box each frame.
          Without this, cycling through proportional glyphs (letters,
          digits, ·) causes the parent flex row to shake horizontally
          on every animation tick. `aria-hidden` keeps AT quiet.        */}
      <span aria-hidden="true" className="invisible" data-counter-placeholder="">
        {value}
      </span>
      {/* Animated display, absolutely positioned on top of the width
          placeholder. Left-aligned so the reveal reads naturally. */}
      <span
        aria-hidden="true"
        className="absolute inset-0 flex items-start justify-start whitespace-pre overflow-hidden"
        data-counter-display=""
      >
        {display}
      </span>
      {/* Accessible: screen readers get the real final value only. */}
      <span className="sr-only">{value}</span>
    </span>
  );
}
